import { useState, useEffect, useMemo, useCallback, Suspense, lazy } from 'react'
import { addDays, isSameDay, startOfDay, isAfter } from 'date-fns'
import { Clock, User, Phone, Home, Store } from 'lucide-react'
import { ref, onValue, push, set } from 'firebase/database'
import styles from '@styles/AppointmentBooking.module.css'
import 'react-date-range/dist/theme/default.css'
import 'react-date-range/dist/styles.css'
import { database } from '@lib/firebase'

const AppointmentBooking = () => {
  const [selectedDate, setSelectedDate] = useState(addDays(new Date(), 1))
  const [existingAppointments, setExistingAppointments] = useState([])
  const [appointmentType, setAppointmentType] = useState('optica')
  const [selectedTime, setSelectedTime] = useState('')
  const [message, setMessage] = useState('')
  const [phone, setPhone] = useState('')
  const [name, setName] = useState('')

  useEffect(() => {
    const appointmentsRef = ref(database, 'citas')
    const unsubscribe = onValue(appointmentsRef, snapshot => {
      const data = snapshot.val()
      if (data) {
        const appointments = Object.entries(data).map(([name, appointmentsByName]) =>
          Object.entries(appointmentsByName).map(([id, appointment]) => ({
            ...appointment,
            id,
            name
          }))
        ).flat()
  
        setExistingAppointments(appointments)
      }
    })

    return () => unsubscribe()
  }, [])

  const handleSelect = date => {
    setSelectedDate(date)
    setSelectedTime('')
  }

  const handleSubmit = e => {
    e.preventDefault()
    const cleanPhone = phone.replace(/\D/g, '')
  
    if (!selectedTime || !name || !cleanPhone) {
      setMessage('Por favor, complete todos los campos.')
      return
    }
  
    const appointmentsRef = ref(database, `citas/${name}`)
    const newAppointmentRef = push(appointmentsRef)
  
    set(newAppointmentRef, {
      date: selectedDate.toISOString(),
      time: selectedTime,
      name,
      phone: cleanPhone,
      type: appointmentType,
      status: 'Pendiente',
    })
      .then(() => {
        setMessage('Cita reservada con éxito. Nos pondremos en contacto contigo pronto para confirmar.')
        setSelectedTime('')
        setName('')
        setPhone('')
      })
      .catch((error) => {
        console.error('Error al guardar la cita:', error)
        setMessage('Hubo un error al reservar la cita. Por favor, inténtelo de nuevo.')
      })
  }

  const getAvailableTimeSlots = useCallback(() => {
    const baseTimeSlots = [
      '10:00', '11:00', '12:00', '1:00', '2:00', '3:00', '4:00', '5:00', '6:00'
    ]

    const appointmentsForDay = existingAppointments.filter(
      appointment => isSameDay(appointment.date, selectedDate) && appointment.status === 'Confirmado'
    )

    if (appointmentType === 'optica') {
      // Para citas en ópticas, simplemente elimine los horarios ya reservados.
      return baseTimeSlots.filter(time => 
        !appointmentsForDay.some(appointment => 
          appointment.time === time && appointment.type === 'optica'
        )
      )
    } else {
      // Para visitas domiciliarias, asegúrese de que haya intervalos de 3 horas entre citas.
      const bookedHomeTimes = appointmentsForDay
        .filter(appointment => appointment.type === 'domicilio')
        .map(appointment => appointment.time)

      return baseTimeSlots.filter(time => {
        const [hours] = time.split(':').map(Number)
        return !bookedHomeTimes.some(bookedTime => {
          const [bookedHours] = bookedTime.split(':').map(Number)
          return Math.abs(hours - bookedHours) < 3
        })
      })
    }
  }, [appointmentType, existingAppointments, selectedDate])

  const availableTimeSlots = useMemo(() => getAvailableTimeSlots(), [getAvailableTimeSlots])
  const Calendar = lazy(() => import('react-date-range').then(module => ({ default: module.Calendar })))

  return (
    <div className={styles.appointmentBooking}>
      <div className={styles.typeSelector}>
        <button
          className={`${styles.typeButton} ${appointmentType === 'optica' ? styles.active : ''}`}
          onClick={() => setAppointmentType('optica')}
        >
          <Store size={20} />
          Reservar cita en óptica
        </button>
        <button
          className={`${styles.typeButton} ${appointmentType === 'domicilio' ? styles.active : ''}`}
          onClick={() => setAppointmentType('domicilio')}
        >
          <Home size={20} />
          Reservar cita a domicilio
        </button>
      </div>

      <div className={styles.bookingContainer}>
        <div className={styles.calendarContainer}>
          <Suspense fallback={<div>Cargando Calendario...</div>}>
          <Calendar
            date={selectedDate}
            onChange={handleSelect}
            minDate={addDays(new Date(), 1)}
            maxDate={addDays(new Date(), 30)}
            disabledDay={(date) => !isAfter(startOfDay(date), startOfDay(new Date()))}
          />
          </Suspense>
        </div>
        <div className={styles.formContainer}>
          <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label htmlFor='time'>
                <Clock size={20} />
                Hora:
              </label>
              <select
                id='time'
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                required
              >
                <option value=''>Selecciona una hora</option>
                {availableTimeSlots.map(time => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
              {availableTimeSlots.length === 0 && (
                <p className={styles.noTimesMessage}>
                  No hay horarios disponibles para este día
                </p>
              )}
            </div>
            <div className={styles.formGroup}>
              <label htmlFor='name'>
                <User size={20} />
                Nombre:
              </label>
              <input
                type='text'
                id='name'
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor='phone'>
                <Phone size={20} />
                Teléfono:
              </label>
              <input
                type='tel'
                id='phone'
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            <button type='submit' className={styles.submitButton}>
              Reservar Cita
            </button>
          </form>
          {message && (
            <p className={`${styles.message} ${message.includes('éxito') ? styles.success : styles.error}`}>
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default AppointmentBooking
